import { useCallback, useEffect, useRef, useState } from 'react';

import type { FalaGerada, IdiomaDoBrain, MotivoDaVozReserva } from '@/data/brain';
import type { FluxoDePcm } from '@/data/fluxoDePcm';

import { TICK_MS, avaliarDuranteAFala, desfechoDaInterrupcao, escutaNova, pedeParaEncerrar } from './interrupcao';
import type { EstadoDaEscuta } from './interrupcao';
import { SINAL_VAZIO, medirSinal } from './medirSinal';
import type { MemoriaDoSinal } from './medirSinal';
import type { SinalDaVoz } from './orbe/sinal';

/**
 * Conversa por voz contínua — porte do `useBrainVoiceMode` do Brain original.
 *
 * Microfone aberto com detecção de fala no navegador: cada pergunta vira uma
 * gravação e uma transcrição (`brain-voz/transcrever`), vai ao Brain como
 * mensagem, e a resposta volta falada (`brain-voz/falar`; sem ela, a voz do
 * navegador). Independente do ditado do campo. Cada transcrição e cada fala
 * contam no teto de IA da clínica — o servidor responde 402 quando estoura.
 *
 * O original tocava a voz em partes (PCM em fluxo); a borda da VecchIA devolve o
 * áudio inteiro, então aqui toca o arquivo — o resto do ciclo é o do original.
 *
 * **Fala curta, texto completo, e interromper falando** (B7, decisão do usuário em
 * 13/09/2026):
 * - a voz fala a `fala` que o servidor devolve no modo voz — poucos pontos, com o aviso
 *   de que a resposta completa ficou no chat. O navegador não resume nada;
 * - enquanto o Brain fala, o microfone continua ouvindo (`interrupcao.ts` decide):
 *   a pessoa falando por cima PAUSA a voz, o Brain ouve a frase e responde a ela na
 *   mesma conversa — a resposta interrompida já está no chat. Ruído, eco ou nada dito:
 *   a voz retoma de onde parou. "Para": a voz não volta;
 * - enquanto a resposta é gerada, o microfone também ouve: a pergunta nova espera a
 *   anterior terminar de gravar (o contexto a inclui) e é respondida logo depois, sem
 *   ler a anterior.
 *
 * **Rápida e natural** (13/09/2026, depois de testar na produção):
 * - nada de "certo, deixa eu ver" logo depois da pergunta: a resposta esperava essa frase
 *   tocar. Só se a resposta demora (`ESPERA_PARA_AVISAR_MS`) ela diz "só um instantinho",
 *   com o áudio gerado no começo da sessão; a saudação é curta e não segura a conversa;
 * - "obrigado, não preciso de mais nada", "tchau", ou "não" depois de "precisa de mais
 *   alguma coisa?" (`pedeParaEncerrar`): despede e fecha;
 * - pausa longa da pessoa depois da resposta (`PAUSA_LONGA_MS`): pergunta, uma vez, se
 *   precisa de mais alguma coisa;
 * - tela acesa durante a conversa (Wake Lock); com a tela apagada, segue enquanto o
 *   sistema mantiver o microfone vivo, e só pausa quando ele cortar.
 *
 * **Mais rápida** (15/09/2026, pedido do usuário): a frase fecha com 800 ms de silêncio, e já com
 * 350 ms o que foi gravado segue para transcrever (`VAD_ADIANTAR_TRANSCRICAO_MS`) — se a pessoa não
 * volta a falar, essa transcrição adiantada vale e o texto chega uns meio segundo antes; se volta,
 * ela é descartada. A espera pela frase dita durante a resposta só acontece quando houve fala.
 *
 * **Tela de voz e decisões** (B9, 13/09/2026): a tela cheia (`ModoDeVoz`) só consome esta sessão —
 * `lerSinal` dá ao orbe a voz real (analisador na saída) ou o microfone; `aoOuvirFrase` deixa a
 * frase decidir a ação em foco ("pode", "agora não") antes de virar mensagem; `anunciar` fala o
 * desfecho sem cortar a VecchIA; `enviarTexto` trata o texto escrito como fala. Abrir e fechar o
 * cartão não recria microfone, áudio nem conversa.
 */

export type FaseDaVoz = 'idle' | 'listening' | 'thinking' | 'speaking' | 'paused';

const MINIMO_DE_CARACTERES = 3;
const MENOR_AUDIO_BYTES = 1200;

/* Detecção de fala: grava a frase inteira e só considera que terminou depois de silêncio real. */
const VAD_TICK_MS = TICK_MS;
const VAD_INICIO_DA_FALA_MS = 150;
/* 1300 ms deixava a resposta lenta; 1000 ms ainda era espera demais (15/09/2026). Com a transcrição
   adiantada, o texto já está a caminho quando a frase fecha. */
const VAD_FIM_POR_SILENCIO_MS = 800;
/**
 * Silêncio que já manda a frase transcrever, sem fechá-la: se a pessoa não voltar a falar até
 * `VAD_FIM_POR_SILENCIO_MS`, a transcrição adiantada vale; se voltar, é descartada.
 */
const VAD_ADIANTAR_TRANSCRICAO_MS = 350;
const VAD_FALA_MINIMA_MS = 400;
const VAD_FALA_MAXIMA_MS = 20000;
const VAD_RECOMECO_SEM_FALA_MS = 8000;
const VAD_LIMIAR_MINIMO = 0.012;
const VAD_MULTIPLICADOR_DO_RUIDO = 2.5;
/** Alguns iPhones entregam só zeros no medidor: depois disso, vira "fale e toque para enviar". */
const VAD_MEDIDOR_MORTO_MS = 2500;
/**
 * Ouvindo há tanto tempo sem ouvir fala nenhuma: avisa o que conferir. Foi o defeito de
 * 13/09/2026 — com o microfone errado, mudo ou baixo demais, a tela ficava em "Ouvindo
 * você…" para sempre (ou passava em silêncio a "toque para enviar"), sem transcrever nada.
 * Tempo de relógio (15/09/2026): contado em ticks, com a página ocupada os 7 s viravam 13–17 s.
 */
const VAD_SEM_FALA_AVISO_MS = 7000;
/** Frase que não termina (barulho alto, ganho automático): avisa para tocar ao terminar. */
const VAD_FRASE_LONGA_AVISO_MS = 12000;
/** No meio da frase, cair abaixo desta fração do pico da própria fala conta como pausa. */
const VAD_FRACAO_DO_PICO = 0.22;
/** Toque no microfone antes disso não envia nada (evita mandar só o clique). */
const TOQUE_MINIMO_MS = 500;
/**
 * Enquanto o Brain fala, a gravação de fundo recomeça a cada tanto se ninguém fala por
 * cima: quando a pessoa interrompe, o começo da frase dela já está gravado, com pouco da
 * voz do Brain antes.
 */
const PRE_ROLAGEM_MS = 3000;
/** A resposta chegou com a pessoa no meio de uma frase: espera a frase acabar, até isto. */
const ESPERA_DA_FRASE_MS = 8000;
/** A resposta demora mais que isto: "só um instantinho", se o áudio dela já está pronto. */
const ESPERA_PARA_AVISAR_MS = 3500;
/** A saudação não segura a conversa: sem o áudio dela pronto até aqui, a VecchIA já ouve. */
const SAUDACAO_ESPERA_MS = 1800;
/** Depois da resposta, este silêncio da pessoa faz a VecchIA perguntar, uma vez, se precisa de mais algo. */
const PAUSA_LONGA_MS = 12000;

type IdiomaCurto = 'pt-BR' | 'en' | 'es';

const SAUDACOES: Record<IdiomaCurto, string> = {
    'pt-BR': 'Oi! Em que posso ajudar?',
    en: 'Hi! How can I help?',
    es: '¡Hola! ¿En qué te ayudo?',
};

/* O que o cartão diz quando o microfone não traz a fala da pessoa — sempre dizendo o que fazer. */
const AVISO_SEM_SINAL = 'O microfone não está mandando som. Confira no cartão se o microfone certo está escolhido e se ele não está mudo — ou fale e toque no microfone quando terminar.';
const AVISO_SEM_FALA = 'Não estou ouvindo a sua voz. Fale mais perto do microfone ou confira no cartão se o microfone certo está escolhido. Se preferir, toque no microfone quando terminar.';
const AVISO_FRASE_LONGA = 'Tem barulho por aí: toque no microfone quando terminar de falar.';
const ERRO_DA_GRAVACAO = 'A gravação do microfone falhou. Toque no microfone para tentar de novo.';

/** O microfone escolhido fica neste navegador: é conveniência, não dado. */
const CHAVE_DO_MICROFONE = 'vecchia.brain.microfone';

/** Só quando a resposta demora (`ESPERA_PARA_AVISAR_MS`), com o áudio gerado no começo da sessão. */
const ESPERAS: Record<IdiomaCurto, string> = {
    'pt-BR': 'Só um instantinho.',
    en: 'Just a moment.',
    es: 'Un momentito.',
};

/** Uma vez, depois de uma pausa longa da pessoa (`PAUSA_LONGA_MS`). */
const MAIS_ALGUMA_COISA: Record<IdiomaCurto, string> = {
    'pt-BR': 'Precisa de ajuda com mais alguma coisa?',
    en: 'Anything else I can help with?',
    es: '¿Te ayudo con algo más?',
};

/** A pessoa encerrou falando (`pedeParaEncerrar`). */
const DESPEDIDAS: Record<IdiomaCurto, string> = {
    'pt-BR': 'Imagina! Qualquer coisa, é só me chamar.',
    en: 'Anytime! Call me if you need anything.',
    es: '¡Con gusto! Cualquier cosa, me llamas.',
};

/** O servidor não mandou a fala (demonstração antiga, servidor anterior): só avisa onde está a resposta. */
const FALA_SEM_RESUMO: Record<IdiomaCurto, string> = {
    'pt-BR': 'Deixei a resposta no chat para você conferir.',
    en: 'I left the answer in the chat for you to check.',
    es: 'Dejé la respuesta en el chat para que la revises.',
};

const OUVI_DURANTE_A_RESPOSTA = 'Ouvi você. Respondo assim que esta resposta terminar.';
const NAO_VOU_LER = 'Certo: esta resposta não vai ser lida. Ela fica no chat.';

function curto(idioma: IdiomaDoBrain): IdiomaCurto {
    return idioma === 'en' || idioma === 'es' ? idioma : 'pt-BR';
}

function langDaFala(idioma: IdiomaDoBrain): string {
    return idioma === 'en' ? 'en-US' : idioma === 'es' ? 'es-ES' : 'pt-BR';
}

function esperar(ms: number): Promise<void> {
    return new Promise((resolve) => { setTimeout(resolve, ms); });
}

export function textoSemMarcacao(texto: string): string {
    return texto
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`[^`]+`/g, ' ')
        .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
        .replace(/[#*_~>|]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/* A fala longa sai em partes: a primeira, curta, para a voz começar em poucos segundos; as
   outras, em blocos de frases inteiras. Num pedido só, a fala de ~40 s levaria de 15 a 25 s
   para o TTS devolver (produção, 13/09/2026: de 6 a 10 s já com 1 a 3 frases). */
const FALA_INTEIRA_ATE = 120;
const PRIMEIRA_PARTE_MAX = 110;
const PARTE_MAX = 280;
const ABREVIACAO_NO_FIM = /(?:^|\s)(?:Dr|Dra|Sr|Sra|Srta|Prof|Profa|Av|Obs|n|nº)\.$/u;

/**
 * Frase termina em `.`, `!`, `?` ou `…` seguidos de espaço — não em "R$ 1.200,00" nem em "Dra.".
 * Sem lookbehind: ele derruba o módulo no Safari anterior ao 16.4.
 */
function frasesDaFala(texto: string): string[] {
    const frases: string[] = [];
    let inicio = 0;
    for (const fim of texto.matchAll(/[.!?…]+(?=\s)/gu)) {
        const ate = (fim.index ?? 0) + fim[0].length;
        if (ABREVIACAO_NO_FIM.test(texto.slice(inicio, ate))) continue;
        frases.push(texto.slice(inicio, ate).trim());
        inicio = ate;
    }
    frases.push(texto.slice(inicio).trim());
    return frases.filter(Boolean);
}

export function partesDaFala(texto: string): string[] {
    const limpo = texto.replace(/\s+/g, ' ').trim();
    if (limpo.length <= FALA_INTEIRA_ATE) return limpo ? [limpo] : [];
    const partes: string[] = [];
    let atual = '';
    for (const frase of frasesDaFala(limpo)) {
        const junto = atual ? `${atual} ${frase}` : frase;
        if (atual && junto.length > (partes.length === 0 ? PRIMEIRA_PARTE_MAX : PARTE_MAX)) {
            partes.push(atual);
            atual = frase;
        } else {
            atual = junto;
        }
    }
    if (atual) partes.push(atual);
    return partes;
}

/*
 * Áudio já gerado das frases curtas (saudação, "só um instantinho", "precisa de mais
 * alguma coisa?", despedida, respostas curtas repetidas): não vai ao TTS de novo. Fica na
 * memória da página, como o cache da borda.
 */
const AUDIO_GUARDADO_ATE = 120;
const MAX_AUDIOS_GUARDADOS = 24;
const audiosGuardados = new Map<string, Promise<FalaGerada | null>>();
const audiosProntos = new Set<string>();

type GerarFala = (texto: string, opcoes: { idioma: IdiomaDoBrain }) => Promise<FalaGerada>;

/** Há o que tocar: o áudio inteiro, ou o fluxo que não terminou vazio. */
function temSom(gerada: FalaGerada): boolean {
    return gerada.fluxo ? !(gerada.fluxo.terminou && gerada.fluxo.amostras === 0) : gerada.audio.size > 0;
}

function gerarGuardado(gerar: GerarFala, texto: string, idioma: IdiomaDoBrain): Promise<FalaGerada | null> {
    if (texto.length > AUDIO_GUARDADO_ATE) return gerar(texto, { idioma }).catch(() => null);
    const chave = `${idioma}|${texto}`;
    const guardado = audiosGuardados.get(chave);
    if (guardado) return guardado;
    const pedido = gerar(texto, { idioma }).then(
        (gerada) => {
            if (temSom(gerada)) audiosProntos.add(chave);
            else audiosGuardados.delete(chave);
            /* Fluxo que caiu no meio não fica guardado pela metade: a próxima vez pede de novo. */
            void gerada.fluxo?.terminado().then(() => {
                if (!gerada.fluxo?.falhou || audiosGuardados.get(chave) !== pedido) return;
                audiosGuardados.delete(chave);
                audiosProntos.delete(chave);
            });
            return gerada;
        },
        () => {
            audiosGuardados.delete(chave);
            return null;
        },
    );
    if (audiosGuardados.size >= MAX_AUDIOS_GUARDADOS) {
        const antiga = audiosGuardados.keys().next().value;
        if (antiga !== undefined) {
            audiosGuardados.delete(antiga);
            audiosProntos.delete(antiga);
        }
    }
    audiosGuardados.set(chave, pedido);
    return pedido;
}

function audioPronto(texto: string, idioma: IdiomaDoBrain): boolean {
    return audiosProntos.has(`${idioma}|${texto}`);
}

function chaveDaFala(texto: string): string {
    return texto.toLowerCase().replace(/[^\w\sáàâãéêíóôõúüç]/gi, '').replace(/\s+/g, ' ').trim();
}

function melhorFormato(): string {
    if (typeof MediaRecorder === 'undefined') return '';
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/wav'].find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

function mensagemDoMicrofone(e: unknown): string {
    const nome = typeof e === 'object' && e !== null && 'name' in e ? String((e as { name: unknown }).name) : '';
    if (nome === 'NotAllowedError' || nome === 'SecurityError') {
        return 'Permita o acesso ao microfone nas configurações do navegador e toque no microfone para tentar de novo.';
    }
    if (nome === 'NotFoundError' || nome === 'OverconstrainedError') return 'Nenhum microfone encontrado neste aparelho.';
    if (nome === 'NotReadableError' || nome === 'AbortError') return 'O microfone está sendo usado por outro app. Feche-o e toque no microfone.';
    return e instanceof Error && e.message ? e.message : 'Não consegui ligar o microfone.';
}

let contextoCompartilhado: AudioContext | null = null;

function contextoDeAudio(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    const Classe = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Classe) return null;
    if (!contextoCompartilhado || contextoCompartilhado.state === 'closed') contextoCompartilhado = new Classe();
    return contextoCompartilhado;
}

/*
 * A voz da VecchIA passa por este analisador a caminho do alto-falante: é dele que o orbe lê a
 * fala real (B9). O som não muda — é um nó a mais no mesmo `AudioContext` da sessão.
 */
const analisadoresDaSaida = new WeakMap<AudioContext, AnalyserNode>();

function saidaDaVoz(ctx: AudioContext): AudioNode {
    const existente = analisadoresDaSaida.get(ctx);
    if (existente) return existente;
    try {
        const analisador = ctx.createAnalyser();
        analisador.fftSize = 1024;
        analisador.smoothingTimeConstant = 0.55;
        analisador.connect(ctx.destination);
        analisadoresDaSaida.set(ctx, analisador);
        return analisador;
    } catch {
        return ctx.destination;
    }
}

/** O celular suspende o áudio e só libera com um toque; tenta retomar sem ficar preso. */
async function garantirAudio(ctx: AudioContext | null, limiteMs = 700): Promise<boolean> {
    if (!ctx) return false;
    if (ctx.state === 'running') return true;
    try {
        await Promise.race([ctx.resume(), new Promise((r) => setTimeout(r, limiteMs))]);
    } catch {
        /* continua suspenso */
    }
    return (ctx.state as string) === 'running';
}

/** Precisa rodar dentro do toque: o navegador só libera o áudio com gesto da pessoa. */
/**
 * Pede a permissão do microfone AINDA DENTRO do toque, e solta na hora.
 *
 * **O caso do iPhone (22/09/2026).** No celular a VecchIA abre já falando, mas quem chama
 * `iniciar()` é um efeito — ele roda quando a lista de modelos chega do servidor, e a essa
 * altura o gesto que abriu a tela já terminou. No Chrome isso passa; no Safari do iOS o
 * pedido de microfone fora do gesto é tratado de outro jeito, e a conversa abre sem ouvir.
 *
 * Então a permissão é pedida no próprio toque — junto do `destravarAudio`, que existe pela
 * mesma razão — e o fluxo já concedido é encerrado imediatamente: o que fica é a
 * autorização, que o `iniciar()` de alguns instantes depois reaproveita.
 *
 * Falha em silêncio de propósito: sem permissão, a tela de voz ainda abre e explica o que
 * houve, como já fazia. Isto é um empurrão, não uma porta.
 */
export function aquecerMicrofone(): void {
    try {
        if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') return;
        void navigator.mediaDevices.getUserMedia({ audio: true })
            .then((fluxo) => fluxo.getTracks().forEach((t) => t.stop()))
            .catch(() => undefined);
    } catch {
        /* sem microfone: o próprio modo de voz dirá */
    }
}

export function destravarAudio(): void {
    try {
        const ctx = contextoDeAudio();
        if (ctx) {
            if (ctx.state !== 'running') void ctx.resume().catch(() => undefined);
            /* No iPhone o resume sozinho nem sempre destrava: um quadro mudo dentro do gesto garante. */
            const buffer = ctx.createBuffer(1, 1, 22050);
            const fonte = ctx.createBufferSource();
            fonte.buffer = buffer;
            fonte.connect(ctx.destination);
            fonte.start(0);
        }
    } catch {
        /* sem Web Audio */
    }
    try {
        if (typeof window !== 'undefined' && window.speechSynthesis?.paused) window.speechSynthesis.resume();
    } catch {
        /* sem síntese */
    }
}

/** A voz do navegador, quando a do servidor falha: feminina, como a da VecchIA. */
function escolherVozDoNavegador(idioma: IdiomaDoBrain): SpeechSynthesisVoice | null {
    if (typeof window === 'undefined' || !window.speechSynthesis) return null;
    const vozes = window.speechSynthesis.getVoices();
    if (vozes.length === 0) return null;
    const prefixo = langDaFala(idioma).split('-')[0]?.toLowerCase() ?? 'pt';
    const doIdioma = vozes.filter((v) => v.lang.toLowerCase().startsWith(prefixo));
    const grupo = doIdioma.length > 0 ? doIdioma : vozes;
    const feminina = /maria|francisca|luciana|female|mulher|feminina|woman|helo[ií]sa|vit[oó]ria|camila/i;
    return grupo.find((v) => feminina.test(v.name)) ?? grupo[0] ?? vozes[0] ?? null;
}

/** O nível do microfone de 0 a 1, em decibéis (de -60 dB a -12 dB), para o medidor do cartão. */
export function nivelDoRms(rms: number): number {
    if (!(rms > 0)) return 0;
    const db = 20 * Math.log10(rms);
    return Math.max(0, Math.min(1, (db + 60) / 48));
}

function lerMicrofoneGuardado(): string {
    try {
        return localStorage.getItem(CHAVE_DO_MICROFONE) ?? '';
    } catch {
        return '';
    }
}

function gravarMicrofoneGuardado(id: string): void {
    try {
        if (id) localStorage.setItem(CHAVE_DO_MICROFONE, id);
        else localStorage.removeItem(CHAVE_DO_MICROFONE);
    } catch {
        /* Sem armazenamento: vale o microfone padrão do sistema. */
    }
}

function vozesProntas(): Promise<void> {
    return new Promise((resolve) => {
        if (typeof window === 'undefined' || !window.speechSynthesis) { resolve(); return; }
        const synth = window.speechSynthesis;
        if (synth.getVoices().length > 0) { resolve(); return; }
        const aoMudar = () => {
            synth.removeEventListener('voiceschanged', aoMudar);
            resolve();
        };
        synth.addEventListener('voiceschanged', aoMudar);
        setTimeout(resolve, 800);
    });
}

export interface RespostaParaVoz {
    ok: boolean;
    /** O texto completo (vai para o chat) ou, sem `ok`, o motivo da falha. */
    resposta: string;
    /** O que a voz fala, vindo do servidor no modo voz. */
    fala?: string | null;
}

export interface OpcoesDaConversaPorVoz {
    idioma: IdiomaDoBrain;
    /** Manda a fala transcrita ao Brain, no modo voz, e devolve a resposta e a fala. */
    enviar: (texto: string) => Promise<RespostaParaVoz>;
    transcrever: (audio: Blob) => Promise<string>;
    /** Sem ela, só a voz do navegador. */
    falar?: (texto: string, opcoes: { idioma: IdiomaDoBrain }) => Promise<FalaGerada>;
    /** Uma sessão de voz começa numa conversa nova. */
    aoIniciarSessao?: () => void;
    /** A pessoa encerrou falando ("obrigado, não preciso de mais nada"): a tela fecha a conversa por voz. */
    aoEncerrar?: () => void;
    /**
     * A frase ouvida (ou escrita) decide algo na tela antes de virar mensagem — hoje, a ação que
     * espera confirmação. Devolve `true` quando decidiu. `comecouEm`: quando a pessoa começou a falar.
     */
    aoOuvirFrase?: (texto: string, contexto: { comecouEm: number; porCimaDaVoz: boolean }) => Promise<boolean>;
    ativa: boolean;
}

export interface MicrofoneDisponivel {
    /** Vazio: o padrão do sistema. */
    id: string;
    nome: string;
}

/**
 * A voz que falou no lugar da Gacrux.
 *
 * O `motivo` vem da borda (`X-Brain-Reserva`) e é o que separa "espere um minuto" de
 * "acabou a cota de hoje" — sem ele a tela dizia só "não respondeu", e quem ouvia
 * concluía que a voz do Gemini tinha parado de funcionar.
 */
export interface VozAlternativa {
    /** `openai` (a reserva da borda) ou `navegador` (a última saída, sem rede). */
    provedor: string;
    motivo: MotivoDaVozReserva | null;
    /** Quando a Gacrux volta a ser tentada, em ISO. */
    ate: string | null;
}

export interface ConversaPorVoz {
    fase: FaseDaVoz;
    transcricaoAoVivo: string;
    ultimaFala: string;
    erro: string | null;
    aviso: string;
    modoManual: boolean;
    suportada: boolean;
    /** Nível do microfone, de 0 a 1, para o medidor do cartão. */
    nivel: number;
    microfones: MicrofoneDisponivel[];
    /** Vazio: o padrão do sistema. */
    microfoneId: string;
    escolherMicrofone: (id: string) => void;
    /** Quem falou quando não foi a Gacrux, e por quê; nulo quando foi ela mesma. */
    vozAlternativa: VozAlternativa | null;
    /** O que o orbe lê a cada quadro (a voz real da VecchIA ou o microfone). Estável entre renders. */
    lerSinal: () => SinalDaVoz;
    /** O espectro atual para a onda de barras (voz real ou microfone); nulo sem analisador. */
    lerEspectro: () => { valores: Uint8Array; hzPorPosicao: number } | null;
    /** Frase curta da sessão ("Pronto.") e volta a ouvir; se ela estiver falando, espera a vez. */
    anunciar: (texto: string) => Promise<void>;
    /** Texto escrito na tela de voz, tratado como fala. */
    enviarTexto: (texto: string) => Promise<void>;
    iniciar: () => Promise<boolean>;
    parar: () => void;
    /** Microfone do painel: envia a fala na hora, interrompe a voz ou retoma a pausa. */
    tocarNoMicrofone: () => void;
}

interface EstadoDoVad {
    ruido: number;
    falaMs: number;
    ouvidoMs: number;
    silencioMs: number;
    fraseMs: number;
    ouviuFala: boolean;
    zerosMs: number;
    ouviuSinal: boolean;
    /** Tempo ouvindo sem fala (para o aviso), o pico da fala desta frase e se já avisou. */
    semFalaMs: number;
    pico: number;
    avisado: boolean;
    /** A frase já foi mandada transcrever adiantada nesta pausa. */
    adiantou: boolean;
}

function estadoNovo(ruido = 0.004): EstadoDoVad {
    return { ruido, falaMs: 0, ouvidoMs: 0, silencioMs: 0, fraseMs: 0, ouviuFala: false, zerosMs: 0, ouviuSinal: false, semFalaMs: 0, pico: 0, avisado: false, adiantou: false };
}

/** A voz que está tocando: pausa (interrupção), retoma (alarme falso) ou encerra. */
interface Reproducao {
    pausar: () => void;
    retomar: () => void;
    encerrar: () => void;
}

export function useConversaPorVoz({ idioma, enviar, transcrever, falar, aoIniciarSessao, aoEncerrar, aoOuvirFrase, ativa }: OpcoesDaConversaPorVoz): ConversaPorVoz {
    const [fase, setFase] = useState<FaseDaVoz>('idle');
    const [transcricaoAoVivo, setTranscricaoAoVivo] = useState('');
    const [ultimaFala, setUltimaFala] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [aviso, setAviso] = useState('');
    const [modoManual, setModoManualEstado] = useState(false);
    const [nivel, setNivel] = useState(0);
    const [microfones, setMicrofones] = useState<MicrofoneDisponivel[]>([]);
    const [microfoneId, setMicrofoneId] = useState<string>(() => lerMicrofoneGuardado());
    const [vozAlternativa, setVozAlternativa] = useState<VozAlternativa | null>(null);
    const microfoneEscolhido = useRef(microfoneId);
    const ultimoNivel = useRef(0);

    const fluxo = useRef<MediaStream | null>(null);
    const gravador = useRef<MediaRecorder | null>(null);
    const partes = useRef<Blob[]>([]);
    const inicioDaGravacao = useRef(0);
    const formato = useRef('audio/webm');
    const medidor = useRef<AnalyserNode | null>(null);
    const fonteDoMedidor = useRef<MediaStreamAudioSourceNode | null>(null);
    const medidorRefeito = useRef(false);
    const manual = useRef(false);
    const vadTimer = useRef<ReturnType<typeof setInterval> | null>(null);
    const vad = useRef<EstadoDoVad | null>(null);
    const faseRef = useRef<FaseDaVoz>('idle');
    const habilitada = useRef(false);
    const reproducao = useRef<Reproducao | null>(null);
    const audioBloqueado = useRef(false);
    const falaPendente = useRef('');
    const processando = useRef(false);
    const sessaoIniciada = useRef(false);
    const ultimaChave = useRef('');
    const ultimaVez = useRef(0);

    /* Interromper falando e ouvir enquanto a resposta é gerada. */
    const vigiaTimer = useRef<ReturnType<typeof setInterval> | null>(null);
    const escuta = useRef<EstadoDaEscuta | null>(null);
    /** A voz foi pausada porque a pessoa falou por cima: guarda o que o Brain dizia (para o eco). */
    const interrupcao = useRef<{ falaDoBrain: string } | null>(null);
    /** Cada pergunta respondida é um turno; um turno velho que acorda depois não mexe em nada. */
    const turno = useRef(0);
    const falaAtual = useRef('');
    /** Muda a cada `calar`: as partes que faltam de uma fala calada não tocam. */
    const vezDaFala = useRef(0);
    const escutandoNaResposta = useRef(false);
    const filaDeFala = useRef('');
    const silenciarProxima = useRef(false);
    const transcrevendo = useRef<Promise<void> | null>(null);
    /** A transcrição adiantada da frase em curso: vale enquanto a pessoa não volta a falar. */
    const adiantada = useRef<{ gravador: MediaRecorder; texto: Promise<string> } | null>(null);
    /** A adiantada da frase que acabou de fechar, à espera de `tratarFrase`. */
    const adiantadaDaFrase = useRef<Promise<string> | null>(null);
    /** A pessoa falou enquanto a resposta era gerada: aí vale esperar a frase dela chegar. */
    const falouNaResposta = useRef(false);
    /** O microfone já trouxe uma frase nesta sessão: o aviso "não estou ouvindo" não cabe mais. */
    const falaJaOuvida = useRef(false);
    /** A VecchIA respondeu e voltou a ouvir: conta a pausa da pessoa desde `ultimaAtividade`. */
    const esperandoDepoisDaResposta = useRef(false);
    const ultimaAtividade = useRef(0);
    /** Já perguntou "precisa de mais alguma coisa?": um "não" agora encerra. */
    const perguntouSeQuerMais = useRef(false);
    const perguntarSeQuerMaisRef = useRef<() => Promise<void>>(async () => undefined);
    const travaDaTela = useRef<WakeLockSentinel | null>(null);
    /** Quando a pessoa começou a falar a frase em curso: a resposta a um cartão só vale se começou depois dele. */
    const inicioDaFrase = useRef(0);
    /** A fala em curso toca pelo `AudioContext` (passa pelo analisador da saída): o orbe lê a voz real. */
    const falaPelaSaida = useRef(false);
    const memoriaDoSinal = useRef<MemoriaDoSinal>({});
    const memoriaDoEspectro = useRef<Uint8Array | null>(null);
    /** Frase curta da sessão ("Pronto.") esperando a VecchIA terminar o que está falando. */
    const anuncioPendente = useRef('');
    const anunciarRef = useRef<(texto: string) => Promise<void>>(async () => undefined);

    const iniciarMicrofoneRef = useRef<() => Promise<void>>(async () => undefined);
    const tratarFraseRef = useRef<(audio: Blob) => Promise<void>>(async () => undefined);
    const iniciarVigiaRef = useRef<(falaDoBrain: string) => void>(() => undefined);
    const interromperRef = useRef<(falaDoBrain: string, jaFalandoMs: number) => void>(() => undefined);
    const retomarFalaRef = useRef<() => void>(() => undefined);
    const executarTurnoRef = useRef<(texto: string, confirmacao?: Promise<boolean>) => Promise<void>>(async () => undefined);

    const opcoes = useRef({ idioma, enviar, transcrever, falar, aoIniciarSessao, aoEncerrar, aoOuvirFrase });
    opcoes.current = { idioma, enviar, transcrever, falar, aoIniciarSessao, aoEncerrar, aoOuvirFrase };

    const mudarFase = useCallback((proxima: FaseDaVoz) => {
        faseRef.current = proxima;
        setFase(proxima);
    }, []);

    const mudarModoManual = useCallback((valor: boolean) => {
        manual.current = valor;
        setModoManualEstado(valor);
    }, []);

    /** O nível do microfone para o medidor, no máximo ~8 vezes por segundo. */
    const publicarNivel = useCallback((rms: number) => {
        const agora = Date.now();
        if (agora - ultimoNivel.current < 120) return;
        ultimoNivel.current = agora;
        setNivel(nivelDoRms(rms));
    }, []);

    const pararVigia = useCallback(() => {
        if (vigiaTimer.current) clearInterval(vigiaTimer.current);
        vigiaTimer.current = null;
        escuta.current = null;
    }, []);

    const pararMicrofone = useCallback(() => {
        pararVigia();
        if (vadTimer.current) clearInterval(vadTimer.current);
        vadTimer.current = null;
        vad.current = null;
        const g = gravador.current;
        gravador.current = null;
        partes.current = [];
        if (g) {
            try {
                g.ondataavailable = null;
                g.onerror = null;
                g.onstop = null;
                if (g.state !== 'inactive') g.stop();
            } catch {
                /* já parado */
            }
        }
    }, [pararVigia]);

    const desligarMedidor = useCallback(() => {
        try {
            fonteDoMedidor.current?.disconnect();
        } catch {
            /* já desligado */
        }
        fonteDoMedidor.current = null;
        medidor.current = null;
    }, []);

    const soltarMicrofone = useCallback(() => {
        pararMicrofone();
        desligarMedidor();
        fluxo.current?.getTracks().forEach((t) => t.stop());
        fluxo.current = null;
        setNivel(0);
    }, [desligarMedidor, pararMicrofone]);

    const calar = useCallback(() => {
        vezDaFala.current += 1;
        const atual = reproducao.current;
        reproducao.current = null;
        atual?.encerrar();
        if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
    }, []);

    /* Tela acesa enquanto a conversa por voz está aberta: apagada, o celular corta o microfone. */
    const manterTelaAcesa = useCallback(async () => {
        if (!habilitada.current || travaDaTela.current || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
        try {
            const trava = await navigator.wakeLock.request('screen');
            if (!habilitada.current) {
                void trava.release().catch(() => undefined);
                return;
            }
            travaDaTela.current = trava;
            trava.addEventListener('release', () => {
                if (travaDaTela.current === trava) travaDaTela.current = null;
            });
        } catch {
            /* recusada (economia de bateria, aba escondida): segue sem */
        }
    }, []);

    const soltarTela = useCallback(() => {
        const trava = travaDaTela.current;
        travaDaTela.current = null;
        if (trava) void trava.release().catch(() => undefined);
    }, []);
    /** O que o orbe lê a cada quadro: a voz real da VecchIA falando, ou o microfone ouvindo. Sem re-render. */
    const lerSinal = useCallback((): SinalDaVoz => {
        const fase = faseRef.current;
        if (fase === 'speaking') {
            const ctx = contextoCompartilhado;
            const saida = ctx ? analisadoresDaSaida.get(ctx) : undefined;
            /* Voz do navegador e `<audio>` não passam pelo analisador: o orbe faz o movimento de fala dele. */
            return falaPelaSaida.current && saida ? medirSinal(saida, 'voz', memoriaDoSinal.current) : SINAL_VAZIO;
        }
        const microfone = medidor.current;
        if ((fase === 'listening' || fase === 'thinking') && microfone) return medirSinal(microfone, 'microfone', memoriaDoSinal.current);
        return SINAL_VAZIO;
    }, []);

    /** O espectro para a onda de barras: o mesmo analisador que o orbe usa, num vetor próprio. Nulo sem analisador. */
    const lerEspectro = useCallback((): { valores: Uint8Array; hzPorPosicao: number } | null => {
        const fase = faseRef.current;
        const ctx = contextoCompartilhado;
        const saida = ctx ? analisadoresDaSaida.get(ctx) : undefined;
        const analisador = fase === 'speaking' ? (falaPelaSaida.current ? saida : undefined) : fase === 'listening' ? medidor.current : null;
        if (!analisador) return null;
        if (memoriaDoEspectro.current?.length !== analisador.frequencyBinCount) memoriaDoEspectro.current = new Uint8Array(analisador.frequencyBinCount);
        const valores = memoriaDoEspectro.current;
        analisador.getByteFrequencyData(valores);
        return { valores, hzPorPosicao: analisador.context.sampleRate / analisador.fftSize };
    }, []);

    /** Toca o áudio decodificado no AudioContext (o caminho que funciona no iPhone). Pausa e retoma do ponto. */
    const tocarBuffer = useCallback((ctx: AudioContext, buffer: AudioBuffer, aoTocar?: () => void) => new Promise<boolean>((resolve) => {
        let fonte: AudioBufferSourceNode | null = null;
        let deslocamento = 0;
        let inicio = 0;
        let pausado = false;
        let resolvido = false;
        let vigia: ReturnType<typeof setTimeout> | null = null;
        const limparVigia = () => {
            if (vigia) clearTimeout(vigia);
            vigia = null;
        };
        const terminar = () => {
            if (resolvido) return;
            resolvido = true;
            limparVigia();
            if (reproducao.current === controle) reproducao.current = null;
            resolve(true);
        };
        const soltarFonte = () => {
            const atual = fonte;
            fonte = null;
            try {
                atual?.stop();
            } catch {
                /* já parou */
            }
        };
        const comecar = () => {
            const nova = ctx.createBufferSource();
            nova.buffer = buffer;
            nova.connect(saidaDaVoz(ctx));
            nova.onended = () => { if (fonte === nova) terminar(); };
            fonte = nova;
            falaPelaSaida.current = true;
            inicio = ctx.currentTime;
            limparVigia();
            /* Se o sistema suspender o áudio no meio, o onended não chega. */
            vigia = setTimeout(terminar, (buffer.duration - deslocamento) * 1000 + 1500);
            try {
                nova.start(0, deslocamento);
                aoTocar?.();
            } catch {
                terminar();
            }
        };
        const controle: Reproducao = {
            pausar: () => {
                if (pausado || resolvido) return;
                pausado = true;
                deslocamento += ctx.currentTime - inicio;
                limparVigia();
                soltarFonte();
            },
            retomar: () => {
                if (!pausado || resolvido) return;
                pausado = false;
                if (deslocamento >= buffer.duration - 0.05) {
                    terminar();
                    return;
                }
                comecar();
            },
            encerrar: () => {
                soltarFonte();
                terminar();
            },
        };
        reproducao.current = controle;
        comecar();
    }), []);

    /** Plano B para navegador sem Web Audio: `<audio>`, com tempo limite. */
    const tocarElemento = useCallback((audio: Blob, aoTocar?: () => void) => new Promise<boolean>((resolve) => {
        falaPelaSaida.current = false;
        const url = URL.createObjectURL(audio);
        const el = new Audio(url);
        el.setAttribute('playsinline', '');
        let resolvido = false;
        let pausado = false;
        let inicio: ReturnType<typeof setTimeout> | null = null;
        let fim: ReturnType<typeof setTimeout> | null = null;
        const terminar = (ok: boolean) => {
            if (resolvido) return;
            resolvido = true;
            if (inicio) clearTimeout(inicio);
            if (fim) clearTimeout(fim);
            URL.revokeObjectURL(url);
            if (reproducao.current === controle) reproducao.current = null;
            resolve(ok);
        };
        const controle: Reproducao = {
            pausar: () => {
                if (pausado || resolvido) return;
                pausado = true;
                if (fim) clearTimeout(fim);
                fim = null;
                el.pause();
            },
            retomar: () => {
                if (!pausado || resolvido) return;
                pausado = false;
                el.play().catch(() => terminar(false));
            },
            encerrar: () => {
                pausado = false;
                try {
                    el.pause();
                } catch {
                    /* já parado */
                }
                terminar(true);
            },
        };
        el.onended = () => terminar(true);
        el.onerror = () => terminar(false);
        el.onpause = () => { if (!el.ended && !pausado) terminar(true); };
        el.onplaying = () => {
            if (inicio) clearTimeout(inicio);
            if (fim) clearTimeout(fim);
            const resta = Number.isFinite(el.duration) ? (el.duration - el.currentTime) * 1000 : 60000;
            fim = setTimeout(() => terminar(true), resta + 2000);
            aoTocar?.();
        };
        reproducao.current = controle;
        inicio = setTimeout(() => terminar(false), 5000);
        el.play().catch((e: unknown) => {
            if (typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'NotAllowedError') audioBloqueado.current = true;
            terminar(false);
        });
    }), []);

    /**
     * A fala que ainda está chegando (B11): cada pedaço agendado colado no anterior, no mesmo
     * `AudioContext` e pela mesma saída que o orbe lê. A pausa guarda a posição em amostras e a
     * retomada agenda dali — o `FluxoDePcm` guarda tudo o que chegou. Resolve `false` se nada tocou.
     */
    const tocarFluxo = useCallback((ctx: AudioContext, fluxo: FluxoDePcm, aoTocar?: () => void) => new Promise<boolean>((resolve) => {
        const taxa = fluxo.taxa;
        const fontes = new Set<AudioBufferSourceNode>();
        /* A próxima amostra a agendar e, na corrida contínua atual, qual amostra toca em qual instante. */
        let cursor = 0;
        let baseAmostra = 0;
        let baseCtx = 0;
        let fimAgendado = 0;
        let comecou = false;
        let avisarAoTocar = true;
        let pausado = false;
        let resolvido = false;
        let vigia: ReturnType<typeof setTimeout> | null = null;
        let pararDeOuvir = () => undefined as void;

        const limparVigia = () => {
            if (vigia) clearTimeout(vigia);
            vigia = null;
        };
        const terminar = () => {
            if (resolvido) return;
            resolvido = true;
            limparVigia();
            pararDeOuvir();
            if (reproducao.current === controle) reproducao.current = null;
            resolve(comecou);
        };
        /* Chegou tudo: se o sistema suspender o áudio, o onended não chega. Ainda chegando e parou
           de chegar: desiste depois de alguns segundos, com o que tocou. */
        const armarVigia = () => {
            limparVigia();
            const restante = Math.max(0, fimAgendado - ctx.currentTime);
            vigia = setTimeout(terminar, restante * 1000 + (fluxo.terminou ? 1500 : 8000));
        };
        const soltarFontes = () => {
            for (const fonte of fontes) {
                fonte.onended = null;
                try {
                    fonte.stop();
                } catch {
                    /* já parou */
                }
            }
            fontes.clear();
        };
        const agendar = () => {
            if (resolvido || pausado) return;
            const disponivel = fluxo.amostras - cursor;
            if (disponivel <= 0) {
                if (fluxo.terminou && !fontes.size) terminar();
                else if (fluxo.terminou) armarVigia();
                return;
            }
            /* Começa com um quarto de segundo na mão: pedaço pequeno demais faz a voz engasgar. */
            if (!comecou && !fluxo.terminou && disponivel < taxa * 0.25) return;
            const agora = ctx.currentTime;
            if (fimAgendado < agora + 0.02) {
                baseCtx = agora + 0.04;
                baseAmostra = cursor;
                fimAgendado = baseCtx;
            }
            const buffer = ctx.createBuffer(1, disponivel, taxa);
            buffer.getChannelData(0).set(fluxo.copiar(cursor, cursor + disponivel));
            const fonte = ctx.createBufferSource();
            fonte.buffer = buffer;
            fonte.connect(saidaDaVoz(ctx));
            fonte.onended = () => {
                fontes.delete(fonte);
                if (!fontes.size && fluxo.terminou && cursor >= fluxo.amostras) terminar();
            };
            try {
                fonte.start(fimAgendado);
            } catch {
                terminar();
                return;
            }
            fontes.add(fonte);
            fimAgendado += disponivel / taxa;
            cursor += disponivel;
            falaPelaSaida.current = true;
            comecou = true;
            if (avisarAoTocar) {
                avisarAoTocar = false;
                aoTocar?.();
            }
            armarVigia();
        };
        const controle: Reproducao = {
            pausar: () => {
                if (pausado || resolvido) return;
                pausado = true;
                cursor = Math.floor(Math.min(cursor, baseAmostra + Math.max(0, ctx.currentTime - baseCtx) * taxa));
                limparVigia();
                soltarFontes();
                fimAgendado = 0;
            },
            retomar: () => {
                if (!pausado || resolvido) return;
                pausado = false;
                if (fluxo.terminou && cursor >= fluxo.amostras - taxa * 0.05) {
                    terminar();
                    return;
                }
                avisarAoTocar = true;
                armarVigia();
                agendar();
            },
            encerrar: () => {
                soltarFontes();
                terminar();
            },
        };
        reproducao.current = controle;
        pararDeOuvir = fluxo.ouvir(agendar);
        armarVigia();
        agendar();
    }), []);

    const tocarAudio = useCallback(async (gerada: FalaGerada, aoTocar?: () => void) => {
        const ctx = contextoDeAudio();
        if (ctx && await garantirAudio(ctx)) {
            if (gerada.fluxo) return tocarFluxo(ctx, gerada.fluxo, aoTocar);
            try {
                const buffer = await ctx.decodeAudioData(await gerada.audio.arrayBuffer());
                if (!habilitada.current) return false;
                return await tocarBuffer(ctx, buffer, aoTocar);
            } catch {
                /* formato não decodificado: tenta o elemento <audio> */
            }
        }
        if (gerada.fluxo) {
            /* Sem Web Audio não há como tocar aos poucos: espera chegar tudo e toca em WAV. */
            await gerada.fluxo.terminado();
            if (!habilitada.current) return false;
            return tocarElemento(gerada.fluxo.emWav(), aoTocar);
        }
        return tocarElemento(gerada.audio, aoTocar);
    }, [tocarBuffer, tocarElemento, tocarFluxo]);

    /**
     * Fala o texto como veio (a `fala` do servidor, a saudação, a espera). `aoTocar` roda quando o som começa.
     * Com `limiteParaComecarMs`, desiste — sem tocar e sem a voz do navegador — se a primeira parte não
     * ficar pronta a tempo.
     */
    const falarTexto = useCallback(async (bruto: string, aoTocar?: () => void, limiteParaComecarMs?: number): Promise<boolean> => {
        const { idioma: lingua, falar: gerar } = opcoes.current;
        const texto = textoSemMarcacao(bruto);
        if (!texto) return false;
        calar();
        setUltimaFala(texto);
        falaAtual.current = texto;

        /* Áudio travado pelo celular: nem gera a voz, espera um toque. */
        const ctx = contextoDeAudio();
        if (ctx && !(await garantirAudio(ctx))) {
            audioBloqueado.current = true;
            return false;
        }

        if (gerar) {
            /* A fala sai em partes (`partesDaFala`), no máximo duas pedidas por vez: a primeira
               começa a tocar logo, e a seguinte já está sendo gerada enquanto esta toca. O
               `aoTocar` roda em cada parte, porque é ele que rearma a escuta por cima da voz.
               Sem voz do servidor na primeira parte, cai para a voz do navegador. */
            const gerarParte = gerar;
            const vez = vezDaFala.current;
            const partesDoTexto = partesDaFala(texto);
            const pedidos: Promise<FalaGerada | null>[] = [];
            const pedir = (i: number): Promise<FalaGerada | null> => {
                const pedido = pedidos[i] ?? gerarGuardado(gerarParte, partesDoTexto[i] ?? '', lingua);
                pedidos[i] = pedido;
                return pedido;
            };
            let tocou = false;
            for (let i = 0; i < partesDoTexto.length; i += 1) {
                const esta = pedir(i);
                if (i + 1 < partesDoTexto.length) void pedir(i + 1);
                const gerada = i === 0 && limiteParaComecarMs
                    ? await Promise.race([esta, esperar(limiteParaComecarMs).then(() => undefined)])
                    : await esta;
                /* Demorou demais para começar: segue a conversa sem esta fala. */
                if (gerada === undefined) return false;
                if (!habilitada.current || vezDaFala.current !== vez) return tocou;
                /* Entre uma parte e outra a pessoa falou por cima: o resto da fala fica no chat. */
                if (tocou && faseRef.current !== 'speaking') return true;
                if (!gerada || !temSom(gerada)) break;
                setVozAlternativa(gerada.provedor && gerada.provedor !== 'gemini'
                    ? { provedor: gerada.provedor, motivo: gerada.reserva ?? null, ate: gerada.reservaAte ?? null }
                    : null);
                if (!(await tocarAudio(gerada, aoTocar))) break;
                tocou = true;
            }
            if (tocou || audioBloqueado.current || !habilitada.current || vezDaFala.current !== vez) return tocou;
        }

        if (typeof window === 'undefined' || !window.speechSynthesis) return false;
        await vozesProntas();
        if (!habilitada.current) return false;
        const synth = window.speechSynthesis;
        setVozAlternativa({ provedor: 'navegador', motivo: null, ate: null });
        falaPelaSaida.current = false;
        return new Promise<boolean>((resolve) => {
            const fala = new SpeechSynthesisUtterance(texto);
            const escolhida = escolherVozDoNavegador(lingua);
            if (escolhida) fala.voice = escolhida;
            fala.lang = langDaFala(lingua);
            fala.rate = 0.95;
            fala.pitch = 1.05;
            let resolvido = false;
            let pausado = false;
            let fim: ReturnType<typeof setTimeout> | null = null;
            /* No celular, sem gesto recente, a fala do navegador simplesmente não começa. */
            const inicio = setTimeout(() => terminar(false), 3000);
            const armarFim = () => {
                if (fim) clearTimeout(fim);
                fim = setTimeout(() => terminar(true), Math.max(14000, texto.length * 100 + 2500));
            };
            const controle: Reproducao = {
                pausar: () => {
                    if (pausado || resolvido) return;
                    pausado = true;
                    if (fim) clearTimeout(fim);
                    fim = null;
                    synth.pause();
                },
                retomar: () => {
                    if (!pausado || resolvido) return;
                    pausado = false;
                    synth.resume();
                    armarFim();
                },
                encerrar: () => {
                    synth.cancel();
                    terminar(true);
                },
            };
            function terminar(ok: boolean) {
                if (resolvido) return;
                resolvido = true;
                clearTimeout(inicio);
                if (fim) clearTimeout(fim);
                if (reproducao.current === controle) reproducao.current = null;
                resolve(ok);
            }
            fala.onstart = () => {
                clearTimeout(inicio);
                armarFim();
                aoTocar?.();
            };
            fala.onend = () => terminar(true);
            fala.onerror = () => terminar(false);
            reproducao.current = controle;
            try {
                if (synth.paused) synth.resume();
                synth.speak(fala);
            } catch {
                terminar(false);
            }
        });
    }, [calar, tocarAudio]);

    /** Para microfone e voz e espera um toque (tela bloqueada, áudio travado, permissão negada). */
    const pausar = useCallback((mensagem = '') => {
        interrupcao.current = null;
        escutandoNaResposta.current = false;
        soltarMicrofone();
        calar();
        setAviso(mensagem);
        setTranscricaoAoVivo('');
        mudarFase('paused');
    }, [calar, mudarFase, soltarMicrofone]);

    const iniciarGravador = useCallback(() => {
        if (!fluxo.current || typeof MediaRecorder === 'undefined') return false;
        const tipo = melhorFormato();
        formato.current = tipo || 'audio/webm';
        partes.current = [];
        try {
            const novo = tipo ? new MediaRecorder(fluxo.current, { mimeType: tipo }) : new MediaRecorder(fluxo.current);
            novo.ondataavailable = (evento: BlobEvent) => {
                if (evento.data.size > 0) partes.current.push(evento.data);
            };
            novo.onerror = () => {
                setErro(ERRO_DA_GRAVACAO);
            };
            gravador.current = novo;
            inicioDaGravacao.current = Date.now();
            novo.start(250);
            return true;
        } catch {
            gravador.current = null;
            return false;
        }
    }, []);

    /** Descarta o que foi gravado sem fala e começa uma gravação limpa. */
    const recomecarGravador = useCallback(() => {
        const g = gravador.current;
        gravador.current = null;
        if (g) {
            try {
                g.ondataavailable = null;
                g.onstop = null;
                if (g.state !== 'inactive') g.stop();
            } catch {
                /* já parado */
            }
        }
        return iniciarGravador();
    }, [iniciarGravador]);

    /**
     * Manda transcrever o que já foi gravado da frase, sem pará-la. O `requestData` fecha o pedaço em
     * curso para o fim da fala entrar; os pedaços do MediaRecorder desde o começo formam um áudio válido.
     */
    const adiantarTranscricao = useCallback((estado: EstadoDoVad) => {
        const g = gravador.current;
        if (!g || g.state !== 'recording') return;
        try {
            g.requestData();
        } catch {
            /* sem requestData: vale o que já chegou em pedaços */
        }
        setTimeout(() => {
            /* A pessoa voltou a falar ou a gravação mudou nesse meio-tempo: não adianta nada. */
            if (gravador.current !== g || vad.current !== estado || !estado.adiantou || !habilitada.current) return;
            const audio = new Blob(partes.current, { type: formato.current });
            if (audio.size < MENOR_AUDIO_BYTES) return;
            const texto = opcoes.current.transcrever(audio).then((t) => t.trim());
            /* Quem usa trata a falha (transcreve a frase inteira); aqui só não fica rejeição solta. */
            texto.catch(() => undefined);
            adiantada.current = { gravador: g, texto };
        }, 30);
    }, []);

    const terminarFrase = useCallback(() => {
        if (vadTimer.current) clearInterval(vadTimer.current);
        vadTimer.current = null;
        vad.current = null;
        const g = gravador.current;
        gravador.current = null;
        const adiantadaAgora = adiantada.current;
        adiantada.current = null;
        adiantadaDaFrase.current = null;
        if (!g || g.state === 'inactive') {
            void iniciarMicrofoneRef.current();
            return;
        }
        /* Só a adiantada desta gravação vale para esta frase. */
        if (adiantadaAgora?.gravador === g) adiantadaDaFrase.current = adiantadaAgora.texto;
        g.onstop = () => {
            const audio = new Blob(partes.current, { type: formato.current });
            partes.current = [];
            void tratarFraseRef.current(audio);
        };
        try {
            g.stop();
        } catch {
            adiantadaDaFrase.current = null;
            void iniciarMicrofoneRef.current();
        }
    }, []);

    const ligarMedidor = useCallback(() => {
        desligarMedidor();
        const ctx = contextoDeAudio();
        if (!ctx || !fluxo.current) return false;
        try {
            const fonte = ctx.createMediaStreamSource(fluxo.current);
            const analisador = ctx.createAnalyser();
            analisador.fftSize = 1024;
            /* Só mede o volume: sem ligar no destino, senão o microfone tocaria no alto-falante. */
            fonte.connect(analisador);
            fonteDoMedidor.current = fonte;
            medidor.current = analisador;
            return true;
        } catch {
            return false;
        }
    }, [desligarMedidor]);

    /**
     * Detecção de fala. Ouve na fase `listening` e, com `escutandoNaResposta`, também
     * enquanto a resposta é gerada. `inicial` continua uma frase que já começou (a
     * interrupção por cima da voz do Brain).
     */
    const iniciarVad = useCallback((inicial?: EstadoDoVad) => {
        if (vadTimer.current) clearInterval(vadTimer.current);
        vad.current = inicial ?? estadoNovo();
        let amostras = new Float32Array(medidor.current?.fftSize ?? 1024);
        let ultimoTick = Date.now();
        vadTimer.current = setInterval(() => {
            /* Com a página ocupada o intervalo atrasa: o aviso sem fala conta o relógio, não os ticks. */
            const agora = Date.now();
            const passouMs = agora - ultimoTick;
            ultimoTick = agora;
            const estado = vad.current;
            const ouvindo = faseRef.current === 'listening' && !processando.current;
            const naResposta = faseRef.current === 'thinking' && escutandoNaResposta.current;
            if (!estado || !habilitada.current || (!ouvindo && !naResposta)) return;

            /* Sem medidor confiável: a pessoa toca no microfone para enviar. */
            const analisador = medidor.current;
            if (manual.current || !analisador) {
                if (ouvindo && Date.now() - inicioDaGravacao.current >= VAD_FALA_MAXIMA_MS) terminarFrase();
                return;
            }
            if (amostras.length !== analisador.fftSize) amostras = new Float32Array(analisador.fftSize);
            analisador.getFloatTimeDomainData(amostras);
            let soma = 0;
            for (const v of amostras) soma += v * v;

            if (soma === 0 && !estado.ouviuSinal) {
                estado.zerosMs += VAD_TICK_MS;
                if (estado.zerosMs >= VAD_MEDIDOR_MORTO_MS) {
                    estado.zerosMs = 0;
                    if (!medidorRefeito.current) {
                        medidorRefeito.current = true;
                        const ctx = contextoDeAudio();
                        if (ctx && ctx.state !== 'running') void ctx.resume().catch(() => undefined);
                        ligarMedidor();
                    } else {
                        mudarModoManual(true);
                        if (!estado.avisado) {
                            estado.avisado = true;
                            setAviso(AVISO_SEM_SINAL);
                        }
                    }
                }
                return;
            }
            estado.ouviuSinal = true;
            const rms = Math.sqrt(soma / amostras.length);
            publicarNivel(rms);
            const limiarBase = Math.max(VAD_LIMIAR_MINIMO, estado.ruido * VAD_MULTIPLICADOR_DO_RUIDO);
            /* No meio da frase, pausa é voltar para perto do fundo: com o ganho automático o
               "silêncio" pode não descer do limiar fixo, então vale também a queda em relação
               ao pico da própria fala. */
            const alto = rms > (estado.ouviuFala ? Math.max(limiarBase, estado.pico * VAD_FRACAO_DO_PICO) : limiarBase);

            if (!estado.ouviuFala) {
                if (alto) {
                    estado.falaMs += VAD_TICK_MS;
                    estado.pico = Math.max(estado.pico, rms);
                    if (estado.falaMs >= VAD_INICIO_DA_FALA_MS) {
                        estado.ouviuFala = true;
                        estado.ouvidoMs = estado.falaMs;
                        if (naResposta) falouNaResposta.current = true;
                        ultimaAtividade.current = Date.now();
                        inicioDaFrase.current = Date.now() - estado.falaMs;
                        if (estado.avisado && ouvindo) {
                            estado.avisado = false;
                            setAviso('');
                        }
                    }
                } else {
                    estado.falaMs = 0;
                    estado.pico = 0;
                    /* O ruído de fundo ajusta o limiar: sala barulhenta exige voz mais alta. */
                    estado.ruido = estado.ruido * 0.95 + rms * 0.05;
                    if (Date.now() - inicioDaGravacao.current > VAD_RECOMECO_SEM_FALA_MS) recomecarGravador();
                    if (ouvindo) {
                        estado.semFalaMs += passouMs;
                        /* Só antes da primeira frase: depois dela o microfone já provou que funciona, e silêncio é a pessoa pensando. */
                        if (!estado.avisado && !falaJaOuvida.current && estado.semFalaMs >= VAD_SEM_FALA_AVISO_MS) {
                            estado.avisado = true;
                            setAviso(AVISO_SEM_FALA);
                        }
                        if (esperandoDepoisDaResposta.current && !perguntouSeQuerMais.current && Date.now() - ultimaAtividade.current >= PAUSA_LONGA_MS) {
                            esperandoDepoisDaResposta.current = false;
                            perguntouSeQuerMais.current = true;
                            void perguntarSeQuerMaisRef.current();
                            return;
                        }
                    }
                }
                return;
            }
            estado.fraseMs += VAD_TICK_MS;
            if (alto) {
                estado.ouvidoMs += VAD_TICK_MS;
                estado.silencioMs = 0;
                estado.pico = Math.max(estado.pico * 0.995, rms);
                /* Voltou a falar: a transcrição adiantada ficou sem o fim da frase. */
                if (estado.adiantou) {
                    estado.adiantou = false;
                    adiantada.current = null;
                }
            } else {
                estado.silencioMs += VAD_TICK_MS;
                if (ouvindo && !estado.adiantou && estado.silencioMs >= VAD_ADIANTAR_TRANSCRICAO_MS && estado.ouvidoMs >= VAD_FALA_MINIMA_MS) {
                    estado.adiantou = true;
                    adiantarTranscricao(estado);
                }
            }
            if (ouvindo && !estado.avisado && estado.fraseMs >= VAD_FRASE_LONGA_AVISO_MS) {
                estado.avisado = true;
                setAviso(AVISO_FRASE_LONGA);
            }
            if (estado.silencioMs >= VAD_FIM_POR_SILENCIO_MS || estado.fraseMs >= VAD_FALA_MAXIMA_MS) {
                if (estado.ouvidoMs < VAD_FALA_MINIMA_MS) {
                    /* Alarme falso por cima da voz do Brain: ela volta de onde parou. */
                    if (interrupcao.current) {
                        if (vadTimer.current) clearInterval(vadTimer.current);
                        vadTimer.current = null;
                        vad.current = null;
                        retomarFalaRef.current();
                        return;
                    }
                    vad.current = { ...estadoNovo(estado.ruido), ouviuSinal: true };
                    recomecarGravador();
                    return;
                }
                terminarFrase();
            }
        }, VAD_TICK_MS);
    }, [adiantarTranscricao, ligarMedidor, mudarModoManual, publicarNivel, recomecarGravador, terminarFrase]);

    /** Enquanto o Brain fala: ouve por cima da voz dele (`interrupcao.ts`) com a gravação de fundo ligada. */
    const iniciarVigia = useCallback((falaDoBrain: string) => {
        pararVigia();
        if (!habilitada.current || manual.current || !medidor.current || !fluxo.current || faseRef.current !== 'speaking') return;
        if (!recomecarGravador()) return;
        escuta.current = escutaNova();
        let amostras = new Float32Array(medidor.current.fftSize);
        let reinicio = Date.now();
        vigiaTimer.current = setInterval(() => {
            const analisador = medidor.current;
            const estado = escuta.current;
            if (!analisador || !estado || !habilitada.current || faseRef.current !== 'speaking') return;
            if (amostras.length !== analisador.fftSize) amostras = new Float32Array(analisador.fftSize);
            analisador.getFloatTimeDomainData(amostras);
            let soma = 0;
            for (const v of amostras) soma += v * v;
            const rmsDaFala = Math.sqrt(soma / amostras.length);
            publicarNivel(rmsDaFala);
            const r = avaliarDuranteAFala(estado, rmsDaFala);
            escuta.current = r.estado;
            if (r.interromper) {
                interromperRef.current(falaDoBrain, r.seguidoMs);
                return;
            }
            if (r.seguidoMs === 0 && Date.now() - reinicio > PRE_ROLAGEM_MS) {
                recomecarGravador();
                reinicio = Date.now();
            }
        }, TICK_MS);
    }, [pararVigia, publicarNivel, recomecarGravador]);
    iniciarVigiaRef.current = iniciarVigia;

    /** A pessoa falou por cima: pausa a voz e segue gravando a frase que já começou. */
    const interromperPorVoz = useCallback((falaDoBrain: string, jaFalandoMs: number) => {
        pararVigia();
        reproducao.current?.pausar();
        interrupcao.current = { falaDoBrain };
        inicioDaFrase.current = Date.now() - jaFalandoMs;
        processando.current = false;
        mudarFase('listening');
        setTranscricaoAoVivo('');
        if (!gravador.current) {
            void iniciarMicrofoneRef.current();
            return;
        }
        iniciarVad({ ...estadoNovo(), ouviuSinal: true, ouviuFala: true, falaMs: jaFalandoMs, ouvidoMs: jaFalandoMs, fraseMs: jaFalandoMs });
    }, [iniciarVad, mudarFase, pararVigia]);
    interromperRef.current = interromperPorVoz;

    /** Não era a pessoa (ruído, eco, nada dito): a voz volta de onde parou. */
    const retomarFala = useCallback(() => {
        const interrompida = interrupcao.current;
        interrupcao.current = null;
        pararMicrofone();
        if (!habilitada.current) return;
        const atual = reproducao.current;
        if (!interrompida || !atual) {
            processando.current = false;
            void iniciarMicrofoneRef.current();
            return;
        }
        processando.current = true;
        mudarFase('speaking');
        setTranscricaoAoVivo('');
        atual.retomar();
        /* A voz do navegador não avisa quando volta: a vigia recomeça daqui (as outras recomeçam no `aoTocar`). */
        iniciarVigiaRef.current(interrompida.falaDoBrain);
    }, [mudarFase, pararMicrofone]);
    retomarFalaRef.current = retomarFala;

    /**
     * Uma frase curta da própria sessão ("Pronto. Agendamento confirmado."). Com a VecchIA pensando
     * ou falando outra coisa, espera a vez (`anuncioPendente`): nada é cortado por causa dela.
     */
    const anunciar = useCallback(async (texto: string) => {
        const frase = texto.trim();
        if (!frase || !habilitada.current || faseRef.current === 'paused') return;
        if (processando.current || faseRef.current === 'speaking' || faseRef.current === 'thinking') {
            anuncioPendente.current = anuncioPendente.current ? `${anuncioPendente.current} ${frase}` : frase;
            return;
        }
        turno.current += 1;
        const meu = turno.current;
        interrupcao.current = null;
        processando.current = true;
        pararMicrofone();
        mudarFase('speaking');
        await falarTexto(frase, () => iniciarVigiaRef.current(frase));
        if (turno.current !== meu) return;
        pararVigia();
        processando.current = false;
        if (!habilitada.current || (faseRef.current as FaseDaVoz) === 'paused') return;
        esperandoDepoisDaResposta.current = true;
        ultimaAtividade.current = Date.now();
        void iniciarMicrofoneRef.current();
    }, [falarTexto, mudarFase, pararMicrofone, pararVigia]);
    anunciarRef.current = anunciar;

    /** Depois de uma pausa longa da pessoa, uma vez só: "precisa de ajuda com mais alguma coisa?". */
    const perguntarSeQuerMais = useCallback(async () => {
        if (!habilitada.current || processando.current || faseRef.current !== 'listening') return;
        turno.current += 1;
        const meu = turno.current;
        processando.current = true;
        pararMicrofone();
        mudarFase('speaking');
        const frase = MAIS_ALGUMA_COISA[curto(opcoes.current.idioma)];
        await falarTexto(frase, () => iniciarVigiaRef.current(frase));
        if (turno.current !== meu) return;
        pararVigia();
        processando.current = false;
        if (!habilitada.current || (faseRef.current as FaseDaVoz) === 'paused') return;
        void iniciarMicrofoneRef.current();
    }, [falarTexto, mudarFase, pararMicrofone, pararVigia]);
    perguntarSeQuerMaisRef.current = perguntarSeQuerMais;

    /** "Só um instantinho" só com o áudio já pronto: a espera nunca atrasa a resposta. */
    const tocarEspera = useCallback((): Promise<boolean> => {
        const lingua = opcoes.current.idioma;
        const frase = ESPERAS[curto(lingua)];
        return audioPronto(frase, lingua) ? falarTexto(frase) : Promise.resolve(false);
    }, [falarTexto]);

    const executarTurno = useCallback(async (texto: string, confirmacao?: Promise<boolean>) => {
        const limpo = texto.trim();
        if (limpo.length < MINIMO_DE_CARACTERES || !habilitada.current) return;
        const chave = chaveDaFala(limpo);
        const agora = Date.now();
        if (chave && chave === ultimaChave.current && agora - ultimaVez.current < 8000) return;
        ultimaChave.current = chave;
        ultimaVez.current = agora;

        turno.current += 1;
        const meu = turno.current;
        processando.current = true;
        pararMicrofone();
        mudarFase('thinking');
        setTranscricaoAoVivo('');
        setErro(null);
        filaDeFala.current = '';
        silenciarProxima.current = false;

        /* Enquanto a resposta é gerada, continua ouvindo: o que a pessoa disser entra na fila. */
        falouNaResposta.current = false;
        escutandoNaResposta.current =!manual.current && Boolean(medidor.current) && iniciarGravador();
        if (escutandoNaResposta.current) iniciarVad();

        esperandoDepoisDaResposta.current = false;
        let fala = '';
        /* Resposta rápida vem direto; demorando, "só um instantinho" com o áudio pronto. */
        const espera: { tocando: Promise<boolean> | null } = { tocando: null };
        const avisoDeEspera = setTimeout(() => {
            if (turno.current === meu && habilitada.current && faseRef.current === 'thinking') espera.tocando = tocarEspera();
        }, ESPERA_PARA_AVISAR_MS);
        try {
            const r = await opcoes.current.enviar(limpo);
            const resposta = r.resposta.trim();
            if (r.ok) fala = r.fala?.trim() || (resposta ? FALA_SEM_RESUMO[curto(opcoes.current.idioma)] : 'Não consegui processar agora. Pode repetir?');
            else fala = resposta || 'Desculpe, tive um problema. Pode repetir?';
        } catch (e) {
            fala = e instanceof Error && e.message ? e.message : 'Desculpe, tive um problema. Pode repetir?';
            setErro(fala);
        }

        clearTimeout(avisoDeEspera);
        /* A espera que já começou termina antes de a resposta começar. */
        if (confirmacao) await confirmacao;
        if (espera.tocando) await espera.tocando;

        /* A pessoa pode estar no meio de uma frase: espera acabar e a transcrição chegar. */
        const limite = Date.now() + ESPERA_DA_FRASE_MS;
        while (escutandoNaResposta.current && vad.current?.ouviuFala && Date.now() < limite && habilitada.current) await esperar(100);
        const ouvia = escutandoNaResposta.current;
        escutandoNaResposta.current = false;
        if (ouvia) {
            /* A frase que acabou de fechar ainda vai chegar em `transcrevendo` — só se houve fala.
               Sem fala nenhuma durante a resposta, a voz começa na hora. */
            if (falouNaResposta.current) await esperar(250);
            const emCurso = transcrevendo.current;
            if (emCurso) await emCurso;
        }
        if (turno.current !== meu) return;
        pararMicrofone();

        if (!habilitada.current) {
            processando.current = false;
            mudarFase('idle');
            return;
        }
        /* Pausou enquanto pensava (tela bloqueada): a resposta fica guardada para o toque. */
        if (faseRef.current === 'paused') {
            falaPendente.current = fala;
            processando.current = false;
            return;
        }

        const proxima = filaDeFala.current;
        filaDeFala.current = '';
        setAviso('');
        if (proxima) {
            /* Já perguntou outra coisa: esta resposta fica no chat e a nova vai na mesma conversa. */
            processando.current = false;
            await executarTurnoRef.current(proxima);
            return;
        }
        if (silenciarProxima.current) {
            silenciarProxima.current = false;
            processando.current = false;
            void iniciarMicrofoneRef.current();
            return;
        }

        mudarFase('speaking');
        audioBloqueado.current = false;
        const falou = await falarTexto(fala, () => iniciarVigiaRef.current(fala));
        /* Interrompida e respondida (ou "para"): o turno novo cuida de tudo. */
        if (turno.current !== meu) return;
        pararVigia();
        if (!habilitada.current) {
            processando.current = false;
            mudarFase('idle');
            return;
        }
        if ((faseRef.current as FaseDaVoz) === 'paused') {
            falaPendente.current = falaPendente.current || fala;
            processando.current = false;
            return;
        }
        if (!falou && audioBloqueado.current) {
            processando.current = false;
            falaPendente.current = fala;
            pausar('A resposta está no chat. Toque no microfone para ouvir e continuar.');
            return;
        }
        processando.current = false;
        setTranscricaoAoVivo('');
        esperandoDepoisDaResposta.current = true;
        ultimaAtividade.current = Date.now();
        void iniciarMicrofoneRef.current();
    }, [falarTexto, iniciarGravador, iniciarVad, mudarFase, pararMicrofone, pararVigia, pausar, tocarEspera]);
    executarTurnoRef.current = executarTurno;

    /** Uma frase completa gravada: por cima da resposta (fila), por cima da voz (interrupção) ou pergunta normal. */
    /** A pessoa encerrou falando: despede curto e a tela fecha a conversa (`aoEncerrar`). */
    const encerrarPorVoz = useCallback(async () => {
        turno.current += 1;
        const meu = turno.current;
        interrupcao.current = null;
        escutandoNaResposta.current = false;
        esperandoDepoisDaResposta.current = false;
        processando.current = true;
        pararMicrofone();
        calar();
        mudarFase('speaking');
        setTranscricaoAoVivo('');
        await falarTexto(DESPEDIDAS[curto(opcoes.current.idioma)]);
        if (turno.current !== meu || !habilitada.current) return;
        processando.current = false;
        if (opcoes.current.aoEncerrar) opcoes.current.aoEncerrar();
        else pausar('Conversa encerrada. Toque no microfone para continuar.');
    }, [calar, falarTexto, mudarFase, pararMicrofone, pausar]);

    const tratarFrase = useCallback(async (audio: Blob) => {
        const jaTranscrevendo = adiantadaDaFrase.current;
        adiantadaDaFrase.current = null;
        if (!habilitada.current || faseRef.current === 'paused') return;
        const interrompida = interrupcao.current;

        if (escutandoNaResposta.current) {
            const trabalho = (async () => {
                let texto = '';
                if (audio.size >= MENOR_AUDIO_BYTES) {
                    try {
                        texto = (await opcoes.current.transcrever(audio)).trim();
                    } catch {
                        texto = '';
                    }
                }
                if (!habilitada.current) return;
                const desfecho = desfechoDaInterrupcao(texto, falaAtual.current);
                if (desfecho === 'responder') {
                    filaDeFala.current = texto;
                    silenciarProxima.current = false;
                    setTranscricaoAoVivo(texto);
                    setAviso(OUVI_DURANTE_A_RESPOSTA);
                } else if (desfecho === 'parar') {
                    filaDeFala.current = '';
                    silenciarProxima.current = true;
                    setAviso(NAO_VOU_LER);
                }
                /* Segue ouvindo: se falar de novo, vale a última. */
                if (escutandoNaResposta.current && habilitada.current && iniciarGravador()) iniciarVad();
            })();
            transcrevendo.current = trabalho;
            await trabalho;
            if (transcrevendo.current === trabalho) transcrevendo.current = null;
            return;
        }

        if (audio.size < MENOR_AUDIO_BYTES) {
            if (interrompida) retomarFala();
            else void iniciarMicrofoneRef.current();
            return;
        }
        processando.current = true;
        mudarFase('thinking');
        let texto = '';
        let falhou = false;
        try {
            /* A transcrição adiantada já está a caminho; se ela falhar, vale a da frase inteira. */
            texto = jaTranscrevendo
                ? await jaTranscrevendo.catch(async () => (await opcoes.current.transcrever(audio)).trim())
                : (await opcoes.current.transcrever(audio)).trim();
        } catch (e) {
            falhou = true;
            const mensagem = e instanceof Error ? e.message : '';
            /* Só o "não havia fala" fica calado (volta a ouvir); o resto — chave faltando, teto,
               servidor fora — aparece no cartão, dizendo o que houve. */
            if (!interrompida && !/entender o áudio|sem fala|curto|short/i.test(mensagem)) setErro(mensagem || 'Não consegui transcrever o áudio. Toque no microfone para tentar de novo.');
        }
        processando.current = false;
        if (!habilitada.current || (faseRef.current as FaseDaVoz) === 'paused') return;

        if (interrompida) {
            const desfecho = falhou ? 'retomar' : desfechoDaInterrupcao(texto, interrompida.falaDoBrain);
            if (desfecho === 'retomar') {
                interrupcao.current = interrompida;
                retomarFala();
                return;
            }
            interrupcao.current = null;
            turno.current += 1;
            calar();
            if (desfecho === 'parar') {
                setTranscricaoAoVivo('');
                void iniciarMicrofoneRef.current();
                return;
            }
        } else if (texto.length < MINIMO_DE_CARACTERES) {
            void iniciarMicrofoneRef.current();
            return;
        }
        setErro(null);
        setTranscricaoAoVivo(texto);
        falaJaOuvida.current = true;
        /* Uma ação espera decisão na tela e a frase decide ("pode", "agora não"): não vira mensagem. */
        const aoOuvir = opcoes.current.aoOuvirFrase;
        if (aoOuvir && await aoOuvir(texto, { comecouEm: inicioDaFrase.current, porCimaDaVoz: Boolean(interrompida) })) {
            processando.current = false;
            if (habilitada.current && (faseRef.current as FaseDaVoz) !== 'paused' && (faseRef.current as FaseDaVoz) !== 'speaking') void iniciarMicrofoneRef.current();
            return;
        }
        /* "Obrigado, não preciso de mais nada", "tchau" — ou "não" logo depois de "precisa de mais alguma coisa?". */
        if (pedeParaEncerrar(texto, { depoisDaPergunta: perguntouSeQuerMais.current })) {
            await encerrarPorVoz();
            return;
        }
        perguntouSeQuerMais.current = false;
        await executarTurno(texto);
    }, [calar, encerrarPorVoz, executarTurno, iniciarGravador, iniciarVad, mudarFase, retomarFala]);
    tratarFraseRef.current = tratarFrase;
    /** Escrito na tela de voz: vale como fala — decide a ação em foco, encerra ou vira pergunta — e a resposta vem em voz. */
    const enviarTexto = useCallback(async (texto: string) => {
        const limpo = texto.trim();
        if (!limpo || !habilitada.current) return;
        destravarAudio();
        interrupcao.current = null;
        escutandoNaResposta.current = false;
        filaDeFala.current = '';
        turno.current += 1;
        calar();
        pararVigia();
        pararMicrofone();
        processando.current = false;
        falaJaOuvida.current = true;
        setAviso('');
        if (faseRef.current === 'paused') mudarFase('idle');
        const aoOuvir = opcoes.current.aoOuvirFrase;
        if (aoOuvir && await aoOuvir(limpo, { comecouEm: Date.now(), porCimaDaVoz: false })) {
            if (habilitada.current && (faseRef.current as FaseDaVoz) !== 'speaking') void iniciarMicrofoneRef.current();
            return;
        }
        if (pedeParaEncerrar(limpo, { depoisDaPergunta: perguntouSeQuerMais.current })) {
            await encerrarPorVoz();
            return;
        }
        perguntouSeQuerMais.current = false;
        await executarTurno(limpo);
        /* A mesma frase repetida logo em seguida (o turno ignora): volta a ouvir. */
        if (habilitada.current && !processando.current && (faseRef.current as FaseDaVoz) === 'idle') void iniciarMicrofoneRef.current();
    }, [calar, encerrarPorVoz, executarTurno, mudarFase, pararMicrofone, pararVigia]);

    /** Os microfones do aparelho, com nome (o navegador só dá os nomes depois da permissão). */
    const listarMicrofones = useCallback(async () => {
        if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') return;
        try {
            const entradas = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput');
            const lista: MicrofoneDisponivel[] = [];
            entradas.forEach((d, i) => {
                const id = d.deviceId === 'default' ? '' : d.deviceId;
                if (!lista.some((m) => m.id === id)) lista.push({ id, nome: d.label || (id ? `Microfone ${String(i + 1)}` : 'Padrão do sistema') });
            });
            setMicrofones(lista);
        } catch {
            /* Sem a lista, fica o microfone padrão — e o cartão não mostra a escolha. */
            setMicrofones([]);
        }
    }, []);

    /** Liga (ou reaproveita) o microfone. No celular o sistema pode encerrar a trilha sozinho. */
    const obterMicrofone = useCallback(async () => {
        const atual = fluxo.current;
        const trilha = atual?.getAudioTracks().find((t) => t.readyState === 'live');
        /* Microfone trocado no cartão: a trilha viva de outro aparelho não serve mais. */
        const doEscolhido = !microfoneEscolhido.current || trilha?.getSettings?.().deviceId === microfoneEscolhido.current;
        if (atual && trilha && doEscolhido) return atual;
        if (atual) {
            desligarMedidor();
            atual.getTracks().forEach((t) => t.stop());
            fluxo.current = null;
        }
        const base = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
        const escolhido = microfoneEscolhido.current;
        let novo: MediaStream;
        try {
            novo = await navigator.mediaDevices.getUserMedia({ audio: escolhido ? { ...base, deviceId: { exact: escolhido } } : base });
        } catch (e) {
            /* O microfone guardado sumiu (fone desligado, outro computador): volta ao padrão. */
            const nome = typeof e === 'object' && e !== null && 'name' in e ? String((e as { name: unknown }).name) : '';
            if (!escolhido || (nome !== 'OverconstrainedError' && nome !== 'NotFoundError')) throw e;
            microfoneEscolhido.current = '';
            setMicrofoneId('');
            gravarMicrofoneGuardado('');
            novo = await navigator.mediaDevices.getUserMedia({ audio: base });
        }
        fluxo.current = novo;
        /* O sistema cortou o microfone (tela bloqueada no iPhone, ligação): pausa dizendo o que houve. */
        const trilhaNova = novo.getAudioTracks()[0];
        if (trilhaNova) {
            const aoCortar = () => {
                if (fluxo.current !== novo || !habilitada.current) return;
                if (faseRef.current === 'paused' || faseRef.current === 'idle') return;
                pausar('Conversa pausada. Toque no microfone para continuar.');
            };
            trilhaNova.addEventListener('ended', aoCortar);
            trilhaNova.addEventListener('mute', () => {
                if (document.visibilityState === 'hidden') aoCortar();
            });
        }
        void listarMicrofones();
        return novo;
    }, [desligarMedidor, listarMicrofones, pausar]);

    const iniciarMicrofone = useCallback(async () => {
        if (!habilitada.current || processando.current || faseRef.current === 'paused') return;
        /* Uma frase da sessão esperava a vez ("Pronto."): fala antes de voltar a ouvir. */
        const anuncio = anuncioPendente.current;
        if (anuncio) {
            anuncioPendente.current = '';
            void anunciarRef.current(anuncio);
            return;
        }
        if (typeof MediaRecorder === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
            setErro('Microfone não suportado neste navegador.');
            return;
        }
        try {
            await obterMicrofone();
            if (!habilitada.current || (faseRef.current as FaseDaVoz) === 'paused') return;
            if (!medidor.current) {
                const ctx = contextoDeAudio();
                if (ctx) await garantirAudio(ctx);
                if (!ligarMedidor()) mudarModoManual(true);
            }
            pararMicrofone();
            if (!habilitada.current || (faseRef.current as FaseDaVoz) === 'paused') return;
            mudarFase('listening');
            setErro(null);
            setAviso('');
            if (!iniciarGravador()) {
                setErro('Não consegui gravar o áudio neste navegador.');
                mudarFase('idle');
                return;
            }
            iniciarVad();
        } catch (e) {
            pausar(mensagemDoMicrofone(e));
        }
    }, [iniciarGravador, iniciarVad, ligarMedidor, mudarFase, mudarModoManual, obterMicrofone, pararMicrofone, pausar]);
    iniciarMicrofoneRef.current = iniciarMicrofone;

    /** Troca o microfone: vale na hora, se estiver ouvindo, e fica guardado neste navegador. */
    const escolherMicrofone = useCallback((id: string) => {
        microfoneEscolhido.current = id;
        setMicrofoneId(id);
        gravarMicrofoneGuardado(id);
        if (!habilitada.current) return;
        const fase = faseRef.current;
        /* Falando ou pensando, o microfone novo entra quando voltar a ouvir (`obterMicrofone`). */
        if (fase !== 'listening' && fase !== 'idle') return;
        soltarMicrofone();
        processando.current = false;
        mudarFase('idle');
        void iniciarMicrofoneRef.current();
    }, [mudarFase, soltarMicrofone]);

    /* Fone ligado ou desligado com a conversa aberta: a lista acompanha. */
    useEffect(() => {
        const md = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
        if (!md || typeof md.addEventListener !== 'function') return undefined;
        const aoMudar = () => { void listarMicrofones(); };
        md.addEventListener('devicechange', aoMudar);
        return () => md.removeEventListener('devicechange', aoMudar);
    }, [listarMicrofones]);

    /** Volta de uma pausa. Roda dentro do toque, então áudio e microfone são liberados. */
    const retomar = useCallback(async () => {
        if (!habilitada.current) return;
        destravarAudio();
        setAviso('');
        setErro(null);
        mudarFase('idle');
        /* Pede o microfone ainda dentro do toque (exigência do iPhone). */
        const microfone = obterMicrofone().then(() => null, (e: unknown) => e);
        const pendente = falaPendente.current;
        falaPendente.current = '';
        if (pendente) {
            const meu = turno.current;
            processando.current = true;
            mudarFase('speaking');
            audioBloqueado.current = false;
            await falarTexto(pendente, () => iniciarVigiaRef.current(pendente));
            if (turno.current !== meu) return;
            pararVigia();
            processando.current = false;
            if (!habilitada.current || (faseRef.current as FaseDaVoz) === 'paused') return;
        }
        const falha = await microfone;
        if (falha) {
            pausar(mensagemDoMicrofone(falha));
            return;
        }
        mudarFase('idle');
        await iniciarMicrofoneRef.current();
    }, [falarTexto, mudarFase, obterMicrofone, pararVigia, pausar]);

    const tocarNoMicrofone = useCallback(() => {
        if (!habilitada.current) return;
        const atual = faseRef.current;
        if (atual === 'paused') {
            void retomar();
            return;
        }
        destravarAudio();
        if (atual === 'speaking') {
            calar();
            return;
        }
        if (atual === 'listening' && gravador.current) {
            if (Date.now() - inicioDaGravacao.current < TOQUE_MINIMO_MS && !interrupcao.current) return;
            terminarFrase();
        }
    }, [calar, retomar, terminarFrase]);

    const parar = useCallback(() => {
        habilitada.current = false;
        processando.current = false;
        turno.current += 1;
        interrupcao.current = null;
        escutandoNaResposta.current = false;
        filaDeFala.current = '';
        silenciarProxima.current = false;
        transcrevendo.current = null;
        adiantada.current = null;
        adiantadaDaFrase.current = null;
        soltarMicrofone();
        calar();
        ultimaChave.current = '';
        ultimaVez.current = 0;
        sessaoIniciada.current = false;
        falaPendente.current = '';
        falaAtual.current = '';
        medidorRefeito.current = false;
        mudarModoManual(false);
        setTranscricaoAoVivo('');
        setUltimaFala('');
        setErro(null);
        setAviso('');
        setVozAlternativa(null);
        falaJaOuvida.current = false;
        esperandoDepoisDaResposta.current = false;
        perguntouSeQuerMais.current = false;
        anuncioPendente.current = '';
        soltarTela();
        mudarFase('idle');
    }, [calar, mudarFase, mudarModoManual, soltarMicrofone, soltarTela]);

    const iniciar = useCallback(async () => {
        habilitada.current = true;
        destravarAudio();
        processando.current = false;
        falaPendente.current = '';
        medidorRefeito.current = false;
        mudarModoManual(false);
        setTranscricaoAoVivo('');
        setUltimaFala('');
        setErro(null);
        setAviso('');
        falaJaOuvida.current = false;
        esperandoDepoisDaResposta.current = false;
        perguntouSeQuerMais.current = false;
        void manterTelaAcesa();

        /* O pedido do microfone sai ainda dentro do toque: no iPhone, fora do gesto, pode falhar. */
        const microfone = typeof navigator.mediaDevices?.getUserMedia === 'function'
            ? obterMicrofone().then(() => null, (e: unknown) => e)
            : Promise.resolve(null);

        if (!sessaoIniciada.current) {
            sessaoIniciada.current = true;
            opcoes.current.aoIniciarSessao?.();
        }
        mudarFase('speaking');
        const falha = await microfone;
        if (!habilitada.current) return false;
        if (falha) {
            pausar(mensagemDoMicrofone(falha));
            return false;
        }
        /* Com o microfone liberado, o medidor já fica pronto para ouvir por cima da saudação. */
        if (!medidor.current) {
            const ctx = contextoDeAudio();
            if (ctx) await garantirAudio(ctx);
            if (!ligarMedidor()) mudarModoManual(true);
        }
        audioBloqueado.current = false;
        const lingua = opcoes.current.idioma;
        const saudacao = SAUDACOES[curto(lingua)];
        const meu = turno.current;
        const saudou = await falarTexto(saudacao, () => iniciarVigiaRef.current(saudacao), SAUDACAO_ESPERA_MS);
        /* As frases fixas ficam prontas já: a espera e a despedida tocam na hora quando precisar. */
        const gerar = opcoes.current.falar;
        if (gerar) for (const frase of [ESPERAS[curto(lingua)], MAIS_ALGUMA_COISA[curto(lingua)], DESPEDIDAS[curto(lingua)]]) void gerarGuardado(gerar, frase, lingua);
        /* Interrompeu a saudação com uma pergunta: o turno dela segue sozinho. */
        if (turno.current !== meu) return true;
        pararVigia();
        if (!habilitada.current || (faseRef.current as FaseDaVoz) === 'paused') return false;
        if (!saudou && audioBloqueado.current) {
            pausar('Toque no microfone para começar.');
            return false;
        }
        processando.current = false;
        mudarFase('idle');
        await iniciarMicrofoneRef.current();
        return true;
    }, [falarTexto, ligarMedidor, manterTelaAcesa, mudarFase, mudarModoManual, obterMicrofone, pararVigia, pausar]);

    useEffect(() => {
        if (!ativa) parar();
    }, [ativa, parar]);

    /*
     * Tela bloqueada ou troca de app. Se o sistema mantiver o microfone e o áudio vivos (Android
     * com Chrome, em geral), a conversa segue; se cortar (iPhone), pausa e espera um toque — o
     * corte também chega pelo `ended`/`mute` da trilha (`obterMicrofone`). Ao voltar, a tela
     * fica acesa de novo.
     */
    useEffect(() => {
        let checagem: ReturnType<typeof setTimeout> | null = null;
        const microfoneVivo = () => {
            const trilha = fluxo.current?.getAudioTracks()[0];
            const ctx = contextoCompartilhado;
            return Boolean(trilha && trilha.readyState === 'live' && !trilha.muted && (!ctx || ctx.state === 'running'));
        };
        const aoMudarVisibilidade = () => {
            if (checagem) clearTimeout(checagem);
            checagem = null;
            if (document.visibilityState !== 'hidden') {
                if (habilitada.current) void manterTelaAcesa();
                return;
            }
            if (!habilitada.current || faseRef.current === 'paused' || faseRef.current === 'idle') return;
            checagem = setTimeout(() => {
                checagem = null;
                if (document.visibilityState !== 'hidden' || !habilitada.current) return;
                if (faseRef.current === 'paused' || faseRef.current === 'idle' || microfoneVivo()) return;
                if (faseRef.current === 'thinking' && processando.current) {
                    escutandoNaResposta.current = false;
                    soltarMicrofone();
                    setAviso('Conversa pausada. Toque no microfone para continuar.');
                    mudarFase('paused');
                    return;
                }
                pausar('Conversa pausada. Toque no microfone para continuar.');
            }, 1500);
        };
        document.addEventListener('visibilitychange', aoMudarVisibilidade);
        return () => {
            if (checagem) clearTimeout(checagem);
            document.removeEventListener('visibilitychange', aoMudarVisibilidade);
        };
    }, [manterTelaAcesa, mudarFase, pausar, soltarMicrofone]);

    useEffect(() => () => parar(), [parar]);

    const suportada = typeof window !== 'undefined'
        && typeof MediaRecorder !== 'undefined'
        && typeof navigator.mediaDevices?.getUserMedia === 'function'
        && (typeof falar === 'function' || typeof window.speechSynthesis !== 'undefined');

    return {
        fase, transcricaoAoVivo, ultimaFala, erro, aviso, modoManual, suportada, nivel, microfones, microfoneId, escolherMicrofone, vozAlternativa,
        iniciar, parar, tocarNoMicrofone, lerSinal, lerEspectro, anunciar, enviarTexto,
    };
}
